/* ==========================================================================
   vw-blocks.js — behaviour for the shared page-block snippets.

   Sections that render any of snippets/vw-block-*.liquid should include:
     <script src="{{ 'vw-blocks.js' | asset_url }}" defer></script>
   (loading it more than once is safe: the file self-guards on window and every
   root element is stamped with data-vw-init).

   Handles:
     .vw-b-faq            accordion (single open, animated height)
     .vw-b-chapter-scroller  active-chapter counter driven by IntersectionObserver
     .vw-b-compare-table__wrap  horizontal-scroll edge affordance
     .vw-b-cover-hero / .vw-b-story-band  scroll parallax on the background image

   DOM scoping: everything is found with document.querySelectorAll on the block
   root class and then queried WITHIN that root. document.currentScript is never
   used (it is null inside Shopify's bundled JS).
   ========================================================================== */
(function () {
  'use strict';

  if (window.vwBlocksLoaded) return;
  window.vwBlocksLoaded = true;

  var FAQ_MS = 350;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function each(selector, fn) {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute('data-vw-init')) continue;
      el.setAttribute('data-vw-init', 'true');
      try { fn(el); } catch (e) { /* one bad block must not kill the rest */ }
    }
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function clamp01(n) {
    return n < 0 ? 0 : (n > 1 ? 1 : n);
  }

  /* ------------------------------- FAQ ---------------------------------- */

  function setFaqOpen(item, panel, btn, open, immediate) {
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      item.classList.add('is-open');
      if (immediate || reduce) {
        panel.style.height = 'auto';
        return;
      }
      panel.style.height = panel.scrollHeight + 'px';
      window.setTimeout(function () {
        if (item.classList.contains('is-open')) panel.style.height = 'auto';
      }, FAQ_MS);
    } else {
      item.classList.remove('is-open');
      if (immediate || reduce) {
        panel.style.height = '0px';
        return;
      }
      panel.style.height = panel.scrollHeight + 'px';
      void panel.offsetHeight; /* force reflow so the collapse animates */
      panel.style.height = '0px';
    }
  }

  function initFaq(root) {
    var items = toArray(root.querySelectorAll('.vw-b-faq__item'));
    if (!items.length) return;
    var openIndex = root.getAttribute('data-open-first') === 'false' ? -1 : 0;

    items.forEach(function (item, i) {
      var btn = item.querySelector('.vw-b-faq__q');
      var panel = item.querySelector('.vw-b-faq__a');
      if (!btn || !panel) return;

      setFaqOpen(item, panel, btn, i === openIndex, true);

      btn.addEventListener('click', function () {
        var wasOpen = item.classList.contains('is-open');
        items.forEach(function (other) {
          if (other === item) return;
          var op = other.querySelector('.vw-b-faq__a');
          var ob = other.querySelector('.vw-b-faq__q');
          if (op && ob && other.classList.contains('is-open')) setFaqOpen(other, op, ob, false, false);
        });
        setFaqOpen(item, panel, btn, !wasOpen, false);
      });
    });
  }

  /* --------------------------- Chapter scroller -------------------------- */

  function initChapters(root) {
    var counter = root.querySelector('[data-vw-chapter-current]');
    var items = toArray(root.querySelectorAll('.vw-b-chapter-scroller__item'));
    if (!items.length || !('IntersectionObserver' in window)) return;

    /* photo variant: crossfading slides, pill, ticks and progress bar */
    var slides = toArray(root.querySelectorAll('[data-vw-chapter-slide]'));
    var ticks = toArray(root.querySelectorAll('[data-vw-chapter-tick]'));
    var pillNum = root.querySelector('[data-vw-chapter-pillnum]');
    var pillTitle = root.querySelector('[data-vw-chapter-pilltitle]');
    var bar = root.querySelector('[data-vw-chapter-bar]');
    var total = items.length;
    var active = -1;

    function setActive(idx) {
      if (idx === active || idx < 0 || idx >= total) return;
      active = idx;
      var item = items[idx];

      /* the counter shows the chapter's OWN number when it has one, so an
         author-numbered list ("01", "02"…) stays authoritative over the index. */
      if (counter) {
        var numEl = item.querySelector('.vw-b-chapter-scroller__num');
        counter.textContent = (numEl && numEl.textContent.trim()) || pad2(idx + 1);
      }

      items.forEach(function (it, j) { it.classList.toggle('is-active', j === idx); });

      slides.forEach(function (s, j) {
        s.classList.toggle('is-active', j === idx);
        s.setAttribute('aria-hidden', j === idx ? 'false' : 'true');
      });
      ticks.forEach(function (t, j) { t.classList.toggle('is-active', j === idx); });

      if (pillNum || pillTitle) {
        var n = item.querySelector('.vw-b-chapter-scroller__num');
        var t = item.querySelector('.vw-b-chapter-scroller__t');
        if (pillNum && n) pillNum.textContent = n.textContent.trim();
        if (pillTitle && t) pillTitle.textContent = t.textContent.trim();
      }
      if (bar) bar.style.width = (((idx + 1) / total) * 100) + '%';
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        var idx = parseInt(entries[i].target.getAttribute('data-vw-index'), 10);
        if (!isNaN(idx)) setActive(idx);
      }
    }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });

    items.forEach(function (it) { io.observe(it); });
    setActive(0);

    /* Mobile stacks the aside and the list into the same grid cell so the photo
       has something to stay pinned against (see critical.css). That overlap means
       the list has to be pushed down past the title + photo by hand. Measure it
       rather than trusting the calc() fallback, which cannot know the real title
       height at this viewport. */
    var media = root.querySelector('[data-vw-chapter-media]');
    var heading = root.querySelector('.vw-b-h2');
    if (media && heading) {
      var setOffset = function () {
        if (window.matchMedia('(min-width: 768px)').matches) {
          root.style.removeProperty('--vw-chap-offset');
          return;
        }
        var h = heading.getBoundingClientRect().height + media.getBoundingClientRect().height;
        root.style.setProperty('--vw-chap-offset', Math.round(h + 32) + 'px');
      };
      setOffset();
      window.addEventListener('resize', setOffset);
      /* the photo defines most of the offset, so remeasure once it has decoded */
      toArray(media.querySelectorAll('img')).forEach(function (img) {
        if (!img.complete) img.addEventListener('load', setOffset, { once: true });
      });
    }
  }

  /* ---------------------------- Compare table ---------------------------- */

  function initCompareTable(wrap) {
    var scroller = wrap.querySelector('.vw-b-compare-table__scroll');
    if (!scroller) return;

    function update() {
      var overflowing = scroller.scrollWidth - scroller.clientWidth > 2;
      wrap.classList.toggle('is-overflowing', overflowing);
      var atEnd = !overflowing || (scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2);
      wrap.classList.toggle('at-end', atEnd);
      wrap.classList.toggle('at-start', scroller.scrollLeft <= 2);
      /* only focusable (for keyboard scrolling) while it actually scrolls */
      if (overflowing) scroller.setAttribute('tabindex', '0');
      else scroller.removeAttribute('tabindex');
    }

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ------------------------------ Parallax ------------------------------- */

  var parallaxTargets = [];
  var parallaxQueued = false;

  function registerParallax(root, mode) {
    var img = root.querySelector(mode === 'hero' ? '.vw-b-cover-hero__img' : '.vw-b-story-band__img');
    if (!img) return;
    parallaxTargets.push({ root: root, img: img, mode: mode });
    if (parallaxTargets.length === 1) {
      window.addEventListener('scroll', queueParallax, { passive: true });
      window.addEventListener('resize', queueParallax, { passive: true });
    }
    queueParallax();
  }

  function queueParallax() {
    if (parallaxQueued) return;
    parallaxQueued = true;
    window.requestAnimationFrame(runParallax);
  }

  function runParallax() {
    parallaxQueued = false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    for (var i = 0; i < parallaxTargets.length; i++) {
      var t = parallaxTargets[i];
      var r = t.root.getBoundingClientRect();
      if (r.height === 0) continue;
      if (r.bottom < -240 || r.top > vh + 240) continue;

      if (t.mode === 'hero') {
        /* framer offset ["start start", "end start"] : 0% -> 14%, scale 1.05 -> 1.16 */
        var p = clamp01(-r.top / r.height);
        t.img.style.transform = 'translate3d(0,' + (p * 14).toFixed(3) + '%,0) scale(' + (1.05 + p * 0.11).toFixed(4) + ')';
      } else {
        /* framer offset ["start end", "end start"] : -8% -> 8% */
        var q = clamp01((vh - r.top) / (r.height + vh));
        t.img.style.transform = 'translate3d(0,' + (-8 + q * 16).toFixed(3) + '%,0)';
      }
    }
  }

  /* --------------------------------- boot -------------------------------- */

  function boot() {
    each('.vw-b-faq', initFaq);
    each('.vw-b-chapter-scroller', initChapters);
    each('.vw-b-compare-table__wrap', initCompareTable);
    if (!reduce) {
      each('.vw-b-cover-hero', function (el) { registerParallax(el, 'hero'); });
      each('.vw-b-story-band', function (el) { registerParallax(el, 'band'); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    /* a re-rendered section drops its old nodes; forget the stale parallax refs */
    parallaxTargets = parallaxTargets.filter(function (t) { return document.body.contains(t.root); });
    boot();
  });
})();
